import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import OpenInNewIcon from '~/assets/images/arrowup-right-primary.svg';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { youTubeUrl } from '~/utils/hosting/constants';

import VideoDialog from './VideoDialog';
import VideoThumbnail from './VideoThumbnail';

interface VideoArticleCardProps {
  titleI18nKey: string;
  descriptionI18nKey: string;
  articleUrl?: string;
  videoUrl?: string;
  embedded?: boolean;
}

export const getYoutubeVideoId = (url: string) => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?#]+)/);
  return match?.[1] || '';
};

const readArticleButtonSx = {
  minWidth: 220,
  padding: 3,
  borderRadius: '24px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '.875rem',
  color: 'secondary.main',
  alignSelf: {
    xs: 'stretch',
    md: 'center',
  },
};

const VideoArticleCard = ({
  titleI18nKey,
  descriptionI18nKey,
  articleUrl = '',
  videoUrl = youTubeUrl,
  embedded = false,
}: VideoArticleCardProps) => {
  const { t } = useTranslation('sites', { keyPrefix: 'howtoCard' });
  const [openVideo, setOpenVideo] = useState(false);

  const { howToArticleUrl, howToWebsitesVideoArticleUrl } = useBrandInfo();

  const learnArticle = articleUrl || howToWebsitesVideoArticleUrl;

  const videoId = getYoutubeVideoId(videoUrl);

  const resolvedThumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  const handleOpenVideo = () => setOpenVideo(true);
  const handleCloseVideo = () => setOpenVideo(false);

  const card = (
    <Card data-testid="video-article-card" sx={{ padding: '12px' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <VideoThumbnail
          title={titleI18nKey ? t(titleI18nKey) : t('title')}
          thumbnailUrl={resolvedThumbnail}
          onPlay={handleOpenVideo}
        />

        <Stack flex={1} spacing={1}>
          <Typography variant="h3" data-testid="video-title">
            {titleI18nKey ? t(titleI18nKey) : t('title')}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="video-description"
            maxWidth="720px"
          >
            {descriptionI18nKey ? t(descriptionI18nKey) : t('description')}
          </Typography>
        </Stack>
        {learnArticle && (
          <LoadingButton
            variant="outlined"
            href={learnArticle}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={
              <Box
                component="img"
                src={OpenInNewIcon}
                alt="Open in new icon"
                width="18px"
                height="18px"
              />
            }
            data-testid="video-read-article-btn"
            sx={readArticleButtonSx}
          >
            {t('buttonLearnMore')}
          </LoadingButton>
        )}
      </Stack>
    </Card>
  );

  const videoDialog = (
    <VideoDialog
      open={openVideo}
      title={titleI18nKey ? t(titleI18nKey) : t('title')}
      embedUrl={embedUrl}
      onClose={handleCloseVideo}
    />
  );

  if (embedded) {
    return (
      <>
        {card}
        {videoDialog}
      </>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h2">{t('header')}</Typography>
        {howToArticleUrl && (
          <Link
            href={howToArticleUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              fontSize: 14,
              textDecoration: 'none',
            }}
            data-testid="video-article-learn-all-link"
          >
            {t('learnAllLink')}
          </Link>
        )}
      </Stack>
      {card}
      {videoDialog}
    </Stack>
  );
};

export default VideoArticleCard;
